import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import {
  NotificationPreferencesService,
  NotificationPreference,
  NotificationPreferenceGroup,
} from 'src/app/shared/services/notification-preferences.service';
import { CommonService } from 'src/app/shared/common.service';

@Component({
  standalone: false,
  selector: 'app-home-notification-settings-dialog',
  templateUrl: './home-notification-settings-dialog.component.html',
  styleUrls: ['./home-notification-settings-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class HomeNotificationSettingsDialogComponent {
  private readonly prefsService = inject(NotificationPreferencesService);
  private readonly commonService = inject(CommonService);
  private readonly messageService = inject(MessageService);
  private readonly dialogRef = inject(DynamicDialogRef, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  readonly embedded = input(false);
  readonly preferences = signal<NotificationPreference[]>(NotificationPreferencesService.createDefaultPreferences());
  readonly preferenceGroups = computed<NotificationPreferenceGroup[]>(() =>
    NotificationPreferencesService.groupPreferences(this.preferences()),
  );
  readonly loading = signal(true);
  readonly saving = signal(false);

  constructor() {
    this.loadPreferences();
  }

  private loadPreferences(): void {
    this.loading.set(true);
    this.prefsService.getPreferences()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: prefs => {
          this.preferences.set(prefs.length ? prefs : NotificationPreferencesService.createDefaultPreferences());
          this.loading.set(false);
        },
        error: () => {
          this.commonService.showErrorMessage(
            'No fue posible cargar las preferencias de notificaci\u00f3n',
            this.messageService,
          );
          this.loading.set(false);
        },
      });
  }

  onToggle(pref: NotificationPreference, enabled: boolean): void {
    this.saving.set(true);
    this.prefsService.updatePreference(pref.kind, enabled)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.preferences.update(preferences => preferences.map(current =>
            current.kind === pref.kind ? { ...current, enabled } : current,
          ));
          this.commonService.showSuccessMessage(
            `Notificaci\u00f3n "${pref.label}" ${enabled ? 'activada' : 'desactivada'}`,
            this.messageService,
          );
          this.saving.set(false);
        },
        error: () => {
          this.commonService.showErrorMessage(
            `No fue posible actualizar la preferencia "${pref.label}"`,
            this.messageService,
          );
          this.saving.set(false);
        },
      });
  }

  close(): void {
    this.dialogRef?.close();
  }
}
